import { useContext, useEffect, useState } from "react";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { CodingInput } from "@medplum/react";

import { Drawer, Group, Paper, Stack, Textarea, UnstyledButton } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import { IconEdit, IconStethoscope } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterContext } from "@/contexts/encounter";
import { NoDataContainer } from "@/encounters/components/NoDataContainer";
import { VitalsDrawerHeader } from "@/encounters/components/VitalSigns/components/VitalsDrawerHeader";
import {
  VitalsFormStack,
  VitalsHeartRateInput,
  VitalsLabeledSection,
  VitalsNumberInput,
  VitalsPairedFields,
} from "@/encounters/components/VitalSigns/components/VitalsFormLayout";
import { VitalsUnitRightSection } from "@/encounters/components/VitalSigns/components/VitalsUnitRightSection";
import { VitalsUnitsToggle } from "@/encounters/components/VitalSigns/components/VitalsUnitsToggle";
import {
  convertHeightMeasurement,
  convertTemperatureValue,
  convertWeightValue,
  getTemperatureUnitSuffix,
  getVitalsUnitFields,
  inferVitalsUnitSystem,
  VitalsUnitSystem,
} from "@/encounters/components/VitalSigns/utils/vitals-unit-system";
import { BodyHeightUnitChoices } from "@/encounters/components/VitalSigns/zod-schemas/vital-sign";
import {
  EncounterAnswersFormType,
  VitalSignsForm,
} from "@/encounters/zod-schemas/encounter-answers";
import { Status } from "@/encounters/zod-schemas/encounters";
import { LOINCValueSets } from "@/fhir/enums/loinc";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";

import { EmptyVitals } from "./components/EmptyVitals";
import { VitalSignsFormReviewWrapper } from "./components/VitalSignsFormReviewWrapper";

type VitalSignsFormSectionProps = {
  editable: boolean;
  form: UseFormReturnType<EncounterAnswersFormType>;
  questionnaireResponse?: QuestionnaireResponse;
  onSubmit: (values: EncounterAnswersFormType) => Promise<void>;
  isFetchingInitialData: boolean;
  isAmendment?: boolean;
};

// Needed because the field label isn't always the same as FHIR's display/text.
// Map is used for ordering the fields in the review section.
export const VITAL_SIGN_FIELD_NAME_TO_LABEL = new Map([
  ["bloodPressure", "Blood Pressure"],
  ["heartRate", "Heart Rate"],
  ["weight", "Weight"],
  ["height", "Height"],
  ["temperature", "Temperature"],
  ["respiratoryRate", "Respiratory Rate"],
  ["comments", "Notes"],
]);

export default function VitalSignsFormSection({
  editable,
  form,
  questionnaireResponse,
  onSubmit,
  isFetchingInitialData,
  isAmendment,
}: VitalSignsFormSectionProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [noData, setNoData] = useState(true);
  const [opened, { open, close }] = useDisclosure(false);
  const { encounter } = useContext(EncounterContext);
  const { hasProviderOrScribeRole } = usePractitionerPermissions();

  useEffect(() => {
    if (!questionnaireResponse) {
      setNoData(true);
      return;
    }

    const vitalSignsItem = questionnaireResponse.item?.find(
      (item) => item.linkId === "encounterVitalSigns",
    );

    setNoData(!vitalSignsItem);
  }, [questionnaireResponse]);

  const onSave = form.onSubmit(async (values) => {
    setIsSaving(true);
    setNoData(false);
    try {
      let formValues = values;

      if (isAmendment) {
        const vitalsData = values.encounterVitalSigns || {};
        const validatedVitals = VitalSignsForm.parse(vitalsData);

        formValues = {
          ...values,
          encounterVitalSigns: validatedVitals,
        };
      }

      await onSubmit(formValues);
    } finally {
      setIsSaving(false);
      close();
    }
  });

  const vitals = form.values?.encounterVitalSigns;

  const unitSystem = inferVitalsUnitSystem({
    weightUnit: vitals?.encounterVitalSignsWeightUnit,
    heightUnit: vitals?.encounterVitalSignsHeightUnit,
    temperatureUnit: vitals?.encounterVitalSignsTemperatureUnit,
  });

  // Units shown alongside the values are read back from the saved fields so they always describe
  // what will be persisted, even for records saved with a mix of unit systems.
  const unitFields = getVitalsUnitFields(unitSystem);
  const weightUnit = vitals?.encounterVitalSignsWeightUnit ?? unitFields.weightUnit;
  const heightUnit = vitals?.encounterVitalSignsHeightUnit ?? unitFields.heightUnit;
  const temperatureUnit = vitals?.encounterVitalSignsTemperatureUnit ?? unitFields.temperatureUnit;

  const showFeetAndInchesInputs = heightUnit === BodyHeightUnitChoices["Feet and Inches"];

  const handleUnitSystemChange = (nextUnitSystem: VitalsUnitSystem) => {
    if (nextUnitSystem === unitSystem) return;

    const nextUnitFields = getVitalsUnitFields(nextUnitSystem);
    const current = form.values.encounterVitalSigns;

    const convertedWeight = convertWeightValue(
      current?.encounterVitalSignsWeight,
      current?.encounterVitalSignsWeightUnit,
      nextUnitSystem,
    );
    const convertedHeight = convertHeightMeasurement(
      {
        heightCm: current?.encounterVitalSignsHeightCm,
        heightFeet: current?.encounterVitalSignsHeightFeet,
        heightInches: current?.encounterVitalSignsHeightInches,
      },
      current?.encounterVitalSignsHeightUnit,
      nextUnitSystem,
    );
    const convertedTemperature = convertTemperatureValue(
      current?.encounterVitalSignsTemperature,
      current?.encounterVitalSignsTemperatureUnit,
      nextUnitSystem,
    );

    form.setFieldValue(
      "encounterVitalSigns.encounterVitalSignsWeightUnit",
      nextUnitFields.weightUnit,
    );
    form.setFieldValue(
      "encounterVitalSigns.encounterVitalSignsHeightUnit",
      nextUnitFields.heightUnit,
    );
    form.setFieldValue(
      "encounterVitalSigns.encounterVitalSignsTemperatureUnit",
      nextUnitFields.temperatureUnit,
    );

    if (convertedWeight !== undefined) {
      form.setFieldValue("encounterVitalSigns.encounterVitalSignsWeight", convertedWeight);
    }
    form.setFieldValue("encounterVitalSigns.encounterVitalSignsHeightCm", convertedHeight.heightCm);
    form.setFieldValue(
      "encounterVitalSigns.encounterVitalSignsHeightFeet",
      convertedHeight.heightFeet,
    );
    form.setFieldValue(
      "encounterVitalSigns.encounterVitalSignsHeightInches",
      convertedHeight.heightInches,
    );
    if (convertedTemperature !== undefined) {
      form.setFieldValue(
        "encounterVitalSigns.encounterVitalSignsTemperature",
        convertedTemperature,
      );
    }
  };

  const shouldShowEmptyVitals = noData && !isAmendment && encounter?.status === Status.Finished;

  return (
    <>
      {!noData ? (
        <Stack
          gap="xs"
          p={isAmendment ? "lg" : ""}
          bg={isAmendment ? "white" : ""}
          style={{ borderRadius: isAmendment ? 8 : 0 }}
        >
          <Group justify="space-between">
            <TextWrapper c="dark.9" variant="bold" data-testid="vitals-title">
              Vitals
            </TextWrapper>
            {(isAmendment || editable) && (
              <PermissionGate requiredPermissions={PROVIDER_SCRIBE_PERMISSION_FLAGS}>
                <UnstyledButton
                  display="flex"
                  onClick={open}
                  style={{ gap: 8, alignItems: "center" }}
                >
                  <IconEdit size={20} color="var(--mantine-color-gray-8)" />
                  <TextWrapper c="dark.9" variant="bold">
                    Edit
                  </TextWrapper>
                </UnstyledButton>
              </PermissionGate>
            )}
          </Group>
          <Paper
            p={24}
            radius="lg"
            aria-label="Vitals"
            id="vital-signs-section"
            style={{ backgroundColor: "white" }}
          >
            <VitalSignsFormReviewWrapper questionnaireResponse={questionnaireResponse} />
          </Paper>
        </Stack>
      ) : shouldShowEmptyVitals ? (
        <EmptyVitals />
      ) : (
        <Stack gap="xs">
          <TextWrapper c="dark.9" variant="bold">
            Vitals
          </TextWrapper>
          <Group
            p="md"
            bg="white"
            justify="space-between"
            style={{ borderRadius: 8 }}
            bd="1px solid var(--mantine-color-cream-4)"
          >
            <NoDataContainer
              onAnchorClick={open}
              text="No vitals added."
              actionTestId="vitals-add-button"
              hideAnchor={!editable && !isAmendment}
              icon={<IconStethoscope size={18} color="var(--mantine-color-gray-6)" />}
              linkText={
                (editable || isAmendment) && hasProviderOrScribeRole ? "Add Vitals Now" : ""
              }
            />
          </Group>
        </Stack>
      )}

      <Drawer.Root size={512} offset={8} opened={opened} onClose={close} position="right">
        <Drawer.Overlay />
        <Drawer.Content>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (editable) {
                onSave();
              }
            }}
          >
            <VitalsDrawerHeader
              isSaving={isSaving}
              showSave={editable}
              closeButtonProps={{ size: "lg" }}
            />
            <Paper px="lg" radius="md" style={{ backgroundColor: "white" }}>
              {isFetchingInitialData ? (
                <Loading />
              ) : (
                <VitalsFormStack>
                  <VitalsUnitsToggle value={unitSystem} onChange={handleUnitSystemChange} />

                  <VitalsLabeledSection label={VITAL_SIGN_FIELD_NAME_TO_LABEL.get("bloodPressure")}>
                    <VitalsPairedFields>
                      <VitalsNumberInput
                        aria-label="Systolic BP"
                        data-testid="vitals-systolic-bp"
                        rightSection={<VitalsUnitRightSection unit="Systolic" />}
                        {...form.getInputProps("encounterVitalSigns.encounterVitalSignsSystolicBP")}
                      />
                      <VitalsNumberInput
                        aria-label="Diastolic BP"
                        data-testid="vitals-diastolic-bp"
                        rightSection={<VitalsUnitRightSection unit="Diastolic" />}
                        {...form.getInputProps(
                          "encounterVitalSigns.encounterVitalSignsDiastolicBP",
                        )}
                      />
                    </VitalsPairedFields>
                  </VitalsLabeledSection>

                  <VitalsHeartRateInput
                    data-testid="vitals-heart-rate"
                    label={VITAL_SIGN_FIELD_NAME_TO_LABEL.get("heartRate")}
                    {...form.getInputProps("encounterVitalSigns.encounterVitalSignsHeartRate")}
                  />

                  <VitalsNumberInput
                    min={1}
                    label={VITAL_SIGN_FIELD_NAME_TO_LABEL.get("weight")}
                    rightSection={<VitalsUnitRightSection unit={weightUnit} />}
                    {...form.getInputProps("encounterVitalSigns.encounterVitalSignsWeight")}
                  />

                  <VitalsLabeledSection label={VITAL_SIGN_FIELD_NAME_TO_LABEL.get("height")}>
                    {showFeetAndInchesInputs ? (
                      <VitalsPairedFields>
                        <VitalsNumberInput
                          min={0}
                          max={9}
                          allowDecimal={false}
                          aria-label="Height feet"
                          data-testid="vitals-height-feet"
                          rightSection={<VitalsUnitRightSection unit="ft" />}
                          {...form.getInputProps(
                            "encounterVitalSigns.encounterVitalSignsHeightFeet",
                          )}
                        />
                        <VitalsNumberInput
                          min={0}
                          max={11.99}
                          allowDecimal
                          aria-label="Height inches"
                          data-testid="vitals-height-inches"
                          rightSection={<VitalsUnitRightSection unit="in" />}
                          {...form.getInputProps(
                            "encounterVitalSigns.encounterVitalSignsHeightInches",
                          )}
                        />
                      </VitalsPairedFields>
                    ) : (
                      <VitalsNumberInput
                        min={1}
                        aria-label="Height"
                        data-testid="vitals-height-cm"
                        rightSection={<VitalsUnitRightSection unit={heightUnit} />}
                        {...form.getInputProps("encounterVitalSigns.encounterVitalSignsHeightCm")}
                      />
                    )}
                  </VitalsLabeledSection>

                  <VitalsNumberInput
                    min={1}
                    label={VITAL_SIGN_FIELD_NAME_TO_LABEL.get("temperature")}
                    rightSection={
                      <VitalsUnitRightSection unit={getTemperatureUnitSuffix(temperatureUnit)} />
                    }
                    {...form.getInputProps("encounterVitalSigns.encounterVitalSignsTemperature")}
                  />
                  <CodingInput
                    path=""
                    label="Method"
                    creatable={false}
                    clearable={false}
                    withHelpText={false}
                    name="temperatureMethod"
                    binding={LOINCValueSets.TemperatureMethod}
                    defaultValue={
                      form.values?.encounterVitalSigns?.encounterVitalSignsTemperatureMethod?.code
                        ? form.values?.encounterVitalSigns?.encounterVitalSignsTemperatureMethod
                        : undefined
                    }
                    {...form.getInputProps(
                      "encounterVitalSigns.encounterVitalSignsTemperatureMethod",
                    )}
                  />

                  <VitalsNumberInput
                    min={1}
                    rightSection={<VitalsUnitRightSection unit="bpm" />}
                    label={VITAL_SIGN_FIELD_NAME_TO_LABEL.get("respiratoryRate")}
                    {...form.getInputProps(
                      "encounterVitalSigns.encounterVitalSignsRespiratoryRate",
                    )}
                  />

                  <Textarea
                    autosize
                    minRows={1}
                    maxRows={3}
                    data-testid="vitals-notes"
                    label={VITAL_SIGN_FIELD_NAME_TO_LABEL.get("comments")}
                    {...form.getInputProps("encounterVitalSigns.encounterVitalSignsVitalsComments")}
                  />
                </VitalsFormStack>
              )}
            </Paper>
          </form>
        </Drawer.Content>
      </Drawer.Root>
    </>
  );
}
